import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { SETTINGS_PERMISSIONS_MATRIX_COLUMNS } from "../../constants/columns.ts"
import {
  type SearchResult,
  buildSearchIndex,
  getPermissionDomId,
  getSectionLayout,
  searchPermissions
} from "../../helpers/layout.ts"
import { fetchPermissionsDisplay } from "../../redux/permissions.thunks.ts.ts"
import type {
  ColumnKey,
  OnBulkChange,
  OnFeaturesToggle,
  PermissionsFlat
} from "../../types.ts"
import type { PermissionFeatures } from "../../types/permissions.types.ts"
import { PermissionsSectionNav } from "./PermissionsSectionNav.tsx"
import { PermissionsSectionPanel } from "./PermissionsSectionPanel.tsx"
import {
  PermissionsEditorContext,
  type PermissionsEditorContextValue
} from "./editor-context.ts"

const HIGHLIGHT_MS = 2400

interface PermissionsEditorProps {
  permissions: PermissionFeatures
  onBulkChange: OnBulkChange
  onFeaturesToggle?: OnFeaturesToggle
  disabled?: boolean
  isUpdating?: boolean
  roleName?: string
  roleUserCount?: number
  headerAction?: React.ReactNode
}

export function PermissionsEditor({
  permissions,
  onBulkChange,
  onFeaturesToggle,
  disabled,
  isUpdating,
  roleName,
  roleUserCount,
  headerAction
}: PermissionsEditorProps) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const displayMap = useAppSelector(
    (state) => state.permissions?.permissionsDisplay ?? null
  )

  useEffect(() => {
    dispatch(fetchPermissionsDisplay())
  }, [dispatch])

  const sections = permissions as unknown as PermissionsFlat
  const layouts = useMemo(
    () =>
      Object.entries(sections).map(([key, section]) =>
        getSectionLayout(key, section, displayMap)
      ),
    [sections, displayMap]
  )

  const [activeKey, setActiveKey] = useState<string | null>(null)
  const currentKey = layouts.some((l) => l.key === activeKey)
    ? activeKey
    : (layouts[0]?.key ?? null)

  const [openState, setOpenState] = useState<Record<string, boolean>>({})
  const [highlightedKey, setHighlightedKey] = useState<string | null>(null)
  const [query, setQuery] = useState("")
  const deferredQuery = useDeferredValue(query)
  const panelRef = useRef<HTMLDivElement>(null)
  const pendingFocus = useRef<string | null>(null)

  const columnLabel = useCallback(
    (col: ColumnKey) => {
      const labelKey = SETTINGS_PERMISSIONS_MATRIX_COLUMNS.find(
        (c) => c.key === col
      )?.labelKey
      return labelKey ? t(labelKey) : col
    },
    [t]
  )
  const searchIndex = useMemo(
    () => buildSearchIndex(layouts, columnLabel),
    [layouts, columnLabel]
  )
  const results = useMemo(
    () => searchPermissions(searchIndex, deferredQuery),
    [searchIndex, deferredQuery]
  )

  const selectSection = (key: string) => {
    setActiveKey(key)
    panelRef.current?.scrollTo({ top: 0 })
  }

  const selectResult = (result: SearchResult) => {
    setActiveKey(result.sectionKey)
    if (result.disclosureId) {
      setOpenState((prev) => ({
        ...prev,
        [result.disclosureId as string]: true
      }))
    }
    setHighlightedKey(result.actionKey)
    pendingFocus.current = result.actionKey
  }

  useEffect(() => {
    const key = pendingFocus.current
    if (!key) return
    pendingFocus.current = null
    const el = document.getElementById(getPermissionDomId(key))
    el?.scrollIntoView?.({ block: "center", behavior: "smooth" })
    el?.focus({ preventScroll: true })
  })

  useEffect(() => {
    if (!highlightedKey) return
    const timer = window.setTimeout(() => setHighlightedKey(null), HIGHLIGHT_MS)
    return () => window.clearTimeout(timer)
  }, [highlightedKey])

  const context = useMemo<PermissionsEditorContextValue>(
    () => ({
      displayMap,
      disabled,
      isUpdating,
      highlightedKey,
      isOpen: (id, defaultOpen = false) => openState[id] ?? defaultOpen,
      setOpen: (id, open) => setOpenState((prev) => ({ ...prev, [id]: open }))
    }),
    [displayMap, disabled, isUpdating, highlightedKey, openState]
  )

  return (
    <PermissionsEditorContext.Provider value={context}>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-border bg-white lg:flex-row">
        <PermissionsSectionNav
          layouts={layouts}
          activeKey={currentKey}
          query={query}
          results={results}
          onQueryChange={setQuery}
          onSelectSection={selectSection}
          onSelectResult={selectResult}
        />
        <div
          ref={panelRef}
          className="relative min-h-0 min-w-0 flex-1 overflow-auto tailwind-scroll bg-muted/60 p-4 transition-opacity duration-200 sm:p-6"
          style={{ opacity: isUpdating ? 0.6 : 1 }}
        >
          {/* Every section stays mounted: the form saves only fields that are
              registered, so an unmounted section would drop out of the save. */}
          {layouts.map((layout) => (
            <div key={layout.key} hidden={layout.key !== currentKey}>
              <PermissionsSectionPanel
                layout={layout}
                section={sections[layout.key as keyof PermissionsFlat]}
                roleName={roleName}
                roleUserCount={roleUserCount}
                headerAction={headerAction}
                onBulkChange={onBulkChange}
                onFeaturesToggle={onFeaturesToggle}
              />
            </div>
          ))}
        </div>
      </div>
    </PermissionsEditorContext.Provider>
  )
}
